import React, { useState, useEffect, useRef } from "react";
import useTranslation from "next-translate/useTranslation";
import ReconnectPopup from "../ReconnectPopup";
import GameButtons from "./gameButtons/GameButtons";
import GameEndedButtons from "./gameButtons/GameEndedButtons";

import { REGULAR_TIER, UNLIMITED_TIER } from "@chesshotel/constants";
import { useGamePlay, sendUserMove, setGameIdSeen } from "../../ducks/gamePlay";
import Head from "next/head";
import { useSettings } from "../../ducks/settings";
import { useMembership } from "../../ducks/membership";

import Moves from "./Moves";
import GameChat from "./GameChat";
import GameAd from "./GameAd";
import OpponentPlayer from "./player/OpponentPlayer";
import OpponentPlayerV2 from "./player/OpponentPlayerV2";
import UserPlayer from "./player/UserPlayer";
import UserPlayerV2 from "./player/UserPlayerV2";
import GamePlayTemplate from "./GamePlayTemplate";
import GameEndPopup from "./popups/GameEndPopup";
import Board from "../../chessBoard/components/Board";
import { useRouter } from "next/router";
import GameSyncPopup from "./popups/GameSyncPopup";
// import { useFeatureIsOn } from "@growthbook/growthbook-react";

export default function GamePlay() {
  const { t } = useTranslation("common");

  let {
    gameId,
    gameEnded,
    positions,
    positionIndex,
    moves,
    possibleMoves,
    playingWhite,
    displayGame,
    leaguePlay,
    status,
    result,
    leagueScoreResult,
    opponent,
    chatMessages,
  } = useGamePlay();

  const settings = useSettings();
  const { autoQueening, boardSize, isDesktop } = settings;
  const router = useRouter();
  const { tier } = useMembership();

  const [chatOpened, setChatOpened] = useState(false);
  const [showChatWindow, setShowChatWindow] = useState(false);

  const [unreadChatMessages, setUnreadChatMessages] = useState(0);
  const chatLength = useRef(chatMessages.length);

  const newPlayer = false; //useFeatureIsOn("mobile-gameplay");

  useEffect(() => {
    if (gameId) {
      setGameIdSeen(gameId);
    }
  }, [gameId]);

  useEffect(() => {
    if (chatMessages.length != chatLength.current) {
      setUnreadChatMessages(chatMessages.length - chatLength.current);
    }
  }, [chatMessages]);

  useEffect(() => {
    if (!gameId) {
      const timer = setTimeout(() => {
        router.push("/lobby");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [gameId, router]);

  function handleShowChatWindow(show) {
    setShowChatWindow(show);
    setUnreadChatMessages(0);
    chatLength.current = chatMessages.length;
  }

  function renderGameButtons() {
    return gameEnded ? (
      <GameEndedButtons
        setChatOpened={setChatOpened}
        handleShowChatWindow={handleShowChatWindow}
        unreadChatMessages={unreadChatMessages}
        isDesktop={isDesktop}
      />
    ) : (
      <GameButtons
        setChatOpened={setChatOpened}
        handleShowChatWindow={handleShowChatWindow}
        unreadChatMessages={unreadChatMessages}
        isDesktop={isDesktop}
      />
    );
  }

  function renderGameAd() {
    return (
      tier !== REGULAR_TIER &&
      tier !== UNLIMITED_TIER && <GameAd isDesktop={isDesktop} />
    );
  }

  let props = {
    isDesktop,
    boardSize,
    leagueScoreResult,
    resultId: result,
    resultTypeId: status,
    gameEnded,
    leaguePlay,
    playingWhite,
    opponentUsername: opponent.displayName,
    chatMessages,
    chatOpened,
    setChatOpened,
    showChatWindow,
    handleShowChatWindow,
    displayGame,
    gameId,
    newPlayer,
  };

  return (
    <>
      <ReconnectPopup />
      <GameSyncPopup />
      <GamePlayTemplate
        OpponentPlayer={newPlayer ? <OpponentPlayerV2 /> : <OpponentPlayer />}
        UserPlayer={newPlayer ? <UserPlayerV2 /> : <UserPlayer />}
        GameChat={<GameChat />}
        Moves={<Moves />}
        GameAd={renderGameAd()}
        GameButtons={renderGameButtons()}
        {...props}
      >
        <Head>
          <title>Chess Hotel</title>
          <meta name="description" content={t("description")} />
          <meta name="keywords" content={t("keywords")} />
        </Head>

        <Board
          size={boardSize}
          fen={positions && positions[positionIndex]}
          lastMove={moves && moves[positionIndex - 1]}
          onMoveMade={sendUserMove}
          autoQueening={autoQueening}
          playingWhite={playingWhite}
          possibleMoves={possibleMoves}
          settings={settings}
        />
      </GamePlayTemplate>
      <GameEndPopup />
    </>
  );
}
